import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, message } from 'antd';
import { useSelector } from 'react-redux';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import regex from '../../constants/system.keys';
import InputTag from '../../components/InputTag';
import LocationSearchLocationIQ from '../../components/LocationSearchLocationIQ';
import { onlyLettersAndNumbers } from '../../helpers/groupByCoors';

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const EditGatewayComponent = ({ onUpdate, data, onFinish, onCancel }) => {
  const { t } = useTranslation();
  const [isLoad, setIsLoad] = useState(false);
  const [form] = Form.useForm();
  const [requestId, setRequestId] = useState(new Date().getTime());
  const [isBlock, setIsBlock] = useState(false);
  const [address, setAddress] = useState({
    note: '',
  });
  const [alertUpdate, setAlertUpdate] = useState({
    visible: false,
    type: null,
    message: null,
  });
  const { updateDevice } = useSelector((state) => state);

  const [updateReturn, setUpdateReturn] = useState();
  useEffect(() => {
    if (updateDevice && updateDevice.requestId === requestId) {
      setUpdateReturn(updateDevice);
    }
  }, [updateDevice]);

  useEffect(() => {
    if (form && data) {
      const { name, serial, note, lat, lng, tags } = data;
      setAddress({
        note,
        lat,
        lng,
      });

      form.setFieldsValue({
        name,
        serial,
        location: note,
        tags: (tags && tags !== ';') ? tags?.split(';') : [],
      });
    }
    return () => {
      setAlertUpdate({ visible: false });
      form.resetFields();
    };
  }, [data, form]);

  const handleAddressChange = (address) => {
    form.setFieldsValue({
      location: address,
    });
    setAddress((state) => ({
      ...state,
      note: address,
    }));
  };

  const handleSelect = (address) => {
    form.setFieldsValue({
      location: address?.note ?? '',
    });
    setAddress(address);
  };

  useEffect(() => {
    if (data && updateReturn && updateReturn.serial === data.serial) {
      setIsLoad(false);
      if (updateReturn.isSuccess) {
        setUpdateReturn(null);
        form.resetFields();
        return onFinish();
      } else {
        setAlertUpdate({
          visible: true,
          type: 'warning',
          message: t('Something went wrong, try again!!'),
        });
      }
    }
  }, [data, form, onFinish, t, updateReturn]);

  const handleOnUpdate = (values) => {
    if (!values.tags || (values.tags && onlyLettersAndNumbers(values.tags?.join('')))) {
      const data = {
        name: values.name,
        note: values.location ?? "",
        serial: values.serial,
        lat: address.lat ?? 0,
        lng: address.lng ?? 0,
        tags: values.tags?.join(';')?.toLowerCase() || ';',
      };
      const dataTimeRequest = new Date().getTime();
      setRequestId(dataTimeRequest)
      setIsLoad(true);
      return onUpdate(data, dataTimeRequest);
    } else {
      message.warning(t('Please enter only letters and numbers!'));
    }
  };

  return (
    <div className="edit-gateway-component">
      <Form
        form={form}
        {...layout}
        name="form_edit_gateway"
        onFinish={(values) => handleOnUpdate(values)}
      >
        <Form.Item
          name="name"
          label={t('Name')}
          rules={[
            {
              required: true,
              message: t('Please input the name of gateway!'),
            },
            {
              pattern: regex.isName,
              message: t('Please enter only letters and numbers!'),
            },
            {
              max: 80,
              message: t('Please input up to 80 characters!'),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="serial"
          label={t('Serial')}
          rules={[
            {
              required: true,
              message: t('Please input the namespace!'),
            },
            {
              pattern: regex.isName,
              message: t('Please enter only letters and numbers!'),
            },
            {
              max: 80,
              message: t('Please input up to 80 characters!'),
            },
          ]}
        >
          <Input disabled={true} />
        </Form.Item>

        <Form.Item
          name="location"
          rules={[
            {
              pattern: regex.isName,
              message: t('Please enter only letters and numbers!'),
            },
            {
              max: 200,
              message: t('Please input up to 200 characters!'),
            },
          ]}
          label={t('Location')}
        >
          <LocationSearchLocationIQ
            address={address.note}
            onChange={handleAddressChange}
            onAddressSelect={handleSelect}
            setIsBlock={setIsBlock}
          />
        </Form.Item>
        <Form.Item
          name="tags"
          className="tags-device"
          label={t('Tag')}
          style={{ borer: '1px solid #434343', borderRadius: '2px' }}
        >
          <InputTag numberTag={3} nameAdd="Add tag" />
        </Form.Item>
        <div className="edit-gateway-alert">
          {alertUpdate.visible && (
            <Alert
              {...alertUpdate}
              showIcon
              closable
              onClose={() => setAlertUpdate({ visible: false })}
            />
          )}
        </div>
        <div className="btn-update-gateway">
          <Button onClick={() => onCancel()}>{t('Cancel')}</Button>
          <Form.Item shouldUpdate={true}>
            {() => (
              <Button
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={isLoad}
                type="primary"
                disabled={
                  !form.isFieldsTouched(true) ||
                  form.getFieldsError().filter(({ errors }) => errors.length)
                    .length
                }
              >
                {t('Update')}
              </Button>
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default EditGatewayComponent;
